/**
 * Canonical model registry for all supported LLM models.
 *
 * Each model is a unique (provider, id) pair with metadata for routing,
 * cost tracking, and capability matching. The MAB router uses armKey()
 * to create per-model arms instead of per-provider arms.
 */

import type { Provider } from "./types";

/** Capability tags for model selection and routing. */
export type ModelCapability =
  | "reasoning"
  | "coding"
  | "creative"
  | "agentic"
  | "structured"
  | "fast"
  | "long-context";

/** Speed classification for latency-sensitive routing decisions. */
export type SpeedClass = "fast" | "medium" | "slow";

/** A single model entry in the registry. */
export interface ModelEntry {
  /** Model identifier as sent to the provider API. */
  id: string;
  /** Which provider hosts this model. */
  provider: Provider;
  /** Human-readable display name for UI. */
  displayName: string;
  /** Capability tags for routing decisions. */
  capabilities: ModelCapability[];
  /** Cost per 1k input tokens in USD. */
  costPer1kInput: number;
  /** Cost per 1k output tokens in USD. */
  costPer1kOutput: number;
  /** Maximum context window in tokens. */
  maxContext: number;
  /** Latency classification. */
  speedClass: SpeedClass;
}

/**
 * The canonical registry of all 14 supported models.
 *
 * Models are grouped by provider for readability. Each entry is a unique
 * (provider, id) pair. Costs are approximate and based on public pricing
 * as of March 2026.
 */
export const MODEL_REGISTRY: readonly ModelEntry[] = [
  // --- Gemini (3 models) ---
  {
    id: "gemini-2.5-pro",
    provider: "gemini",
    displayName: "Gemini 2.5 Pro",
    capabilities: ["reasoning", "creative", "agentic", "long-context"],
    costPer1kInput: 0.00125,
    costPer1kOutput: 0.005,
    maxContext: 1_000_000,
    speedClass: "medium",
  },
  {
    id: "gemini-2.5-flash",
    provider: "gemini",
    displayName: "Gemini 2.5 Flash",
    capabilities: ["fast", "structured", "creative"],
    costPer1kInput: 0.0003,
    costPer1kOutput: 0.0025,
    maxContext: 1_000_000,
    speedClass: "fast",
  },
  // Fastest + cheapest Gemini 2.5 model. GA as of mid-2026.
  // Same input price as Flash but 6x cheaper output. Supports implicit caching
  // (auto-deduplication of repeated prefix tokens, no code changes needed).
  {
    id: "gemini-2.5-flash-lite",
    provider: "gemini",
    displayName: "Gemini 2.5 Flash-Lite",
    capabilities: ["fast", "structured", "creative"],
    costPer1kInput: 0.0001,
    costPer1kOutput: 0.0004,
    maxContext: 1_000_000,
    speedClass: "fast",
  },

  // --- Claude (1 model) ---
  {
    id: "claude-sonnet-4-5-20250929",
    provider: "claude",
    displayName: "Claude Sonnet 4.5",
    capabilities: ["reasoning", "creative", "coding", "agentic"],
    costPer1kInput: 0.003,
    costPer1kOutput: 0.015,
    maxContext: 200_000,
    speedClass: "medium",
  },

  // --- OpenAI (2 models) ---
  {
    id: "gpt-4o",
    provider: "openai",
    displayName: "GPT-4o",
    capabilities: ["reasoning", "creative", "coding", "structured"],
    costPer1kInput: 0.0025,
    costPer1kOutput: 0.01,
    maxContext: 128_000,
    speedClass: "medium",
  },
  {
    id: "gpt-4o-mini",
    provider: "openai",
    displayName: "GPT-4o Mini",
    capabilities: ["fast", "structured", "creative"],
    costPer1kInput: 0.00015,
    costPer1kOutput: 0.0006,
    maxContext: 128_000,
    speedClass: "fast",
  },

  // --- Fireworks (5 models) ---
  {
    id: "accounts/fireworks/models/gpt-oss-120b",
    provider: "fireworks",
    displayName: "GPT-OSS 120B (FW)",
    capabilities: ["reasoning", "creative", "coding"],
    costPer1kInput: 0.0009,
    costPer1kOutput: 0.0009,
    maxContext: 131_072,
    speedClass: "medium",
  },
  {
    id: "accounts/fireworks/models/gpt-oss-20b",
    provider: "fireworks",
    displayName: "GPT-OSS 20B (FW)",
    capabilities: ["fast", "structured"],
    costPer1kInput: 0.0002,
    costPer1kOutput: 0.0002,
    maxContext: 131_072,
    speedClass: "fast",
  },
  {
    id: "accounts/fireworks/models/kimi-k2-instruct",
    provider: "fireworks",
    displayName: "Kimi K2 Instruct (FW)",
    capabilities: ["reasoning", "coding", "agentic"],
    costPer1kInput: 0.0009,
    costPer1kOutput: 0.0009,
    maxContext: 131_072,
    speedClass: "medium",
  },
  {
    id: "accounts/fireworks/models/deepseek-v3p2",
    provider: "fireworks",
    displayName: "DeepSeek V3.2 (FW)",
    capabilities: ["fast", "coding", "structured"],
    costPer1kInput: 0.0002,
    costPer1kOutput: 0.0002,
    maxContext: 131_072,
    speedClass: "fast",
  },
  {
    id: "accounts/fireworks/models/glm-5",
    provider: "fireworks",
    displayName: "GLM-5 (FW)",
    capabilities: ["reasoning", "creative", "structured"],
    costPer1kInput: 0.0009,
    costPer1kOutput: 0.0009,
    maxContext: 131_072,
    speedClass: "medium",
  },

  // --- DeepSeek (1 model) ---
  {
    id: "deepseek-chat",
    provider: "deepseek",
    displayName: "DeepSeek Chat",
    capabilities: ["fast", "coding", "reasoning"],
    costPer1kInput: 0.00027,
    costPer1kOutput: 0.0011,
    maxContext: 64_000,
    speedClass: "fast",
  },

  // --- Together (2 models) ---
  {
    id: "meta-llama/Llama-3.3-70B-Instruct-Turbo",
    provider: "together",
    displayName: "Llama 3.3 70B Turbo (TG)",
    capabilities: ["fast", "creative", "structured"],
    costPer1kInput: 0.00088,
    costPer1kOutput: 0.00088,
    maxContext: 131_072,
    speedClass: "fast",
  },
  {
    id: "openai/gpt-oss-120B",
    provider: "together",
    displayName: "GPT-OSS 120B (TG)",
    capabilities: ["reasoning", "creative", "coding"],
    costPer1kInput: 0.0009,
    costPer1kOutput: 0.0009,
    maxContext: 131_072,
    speedClass: "medium",
  },
] as const;

/**
 * Get all models available for a given provider.
 */
export function getModelsForProvider(provider: Provider): ModelEntry[] {
  return MODEL_REGISTRY.filter((m) => m.provider === provider);
}

/**
 * Look up a specific model by provider and model ID.
 * Returns undefined if not found.
 */
export function getModelEntry(provider: Provider, modelId: string): ModelEntry | undefined {
  return MODEL_REGISTRY.find((m) => m.provider === provider && m.id === modelId);
}

/**
 * Generate a unique arm key for the MAB router.
 * Format: "provider:modelId" (e.g., "fireworks:accounts/fireworks/models/gpt-oss-120b").
 */
export function armKey(provider: Provider, modelId: string): string {
  return `${provider}:${modelId}`;
}
